import {
  Component,
  Input,
  OnChanges,
  DestroyRef,
  inject,
  SimpleChanges,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { hasInputChanged } from "@shared/helpers/hasChanges/hasChanges.helper";
import { merge, tap } from "rxjs";
import { InputNumberModule } from "primeng/inputnumber";
import {
  CollaboratorRoleEnum,
  CashLimitAction,
  CoordinatorProfile,
  PdvAccessMetadata,
  PdvAccessSettings,
  PdvSalesScope,
} from "@models/collaborator";

export function pdvAccessControls(
  person?: PdvAccessMetadata & {
    role?: CollaboratorRoleEnum;
    pdv_sales_scope?: PdvSalesScope;
  },
) {
  return {
    role: new FormControl(person?.role ?? "", {
      nonNullable: true,
      validators: [Validators.required],
    }),
    coordinatorProfile: new FormControl<CoordinatorProfile>(
      person?.coordinator_profile ?? "approver_only",
      { nonNullable: true },
    ),
    pdvSalesScope: new FormControl<PdvSalesScope>(
      person?.pdv_sales_scope ?? "portaria_only",
      { nonNullable: true },
    ),
    salesLimit: new FormControl<number | null>(
      person?.sales_limit_cents == null ? null : person.sales_limit_cents / 100,
    ),
    cashLimitAction: new FormControl<CashLimitAction>(
      person?.cash_limit_action ?? "notify_only",
      { nonNullable: true },
    ),
  };
}
export type PdvAccessControls = ReturnType<typeof pdvAccessControls>;

export function pdvAccessPayload(
  controls: PdvAccessControls,
): PdvAccessSettings {
  const role = controls.role.value as CollaboratorRoleEnum;
  const operates =
    role === CollaboratorRoleEnum.PDV_PORTARIA ||
    ((role === CollaboratorRoleEnum.PDV_PORTARIA_COORDINATOR ||
      role === CollaboratorRoleEnum.MANAGER) &&
      controls.coordinatorProfile.value === "also_operates");
  return {
    role,
    ...(role === CollaboratorRoleEnum.PDV_PORTARIA_COORDINATOR ||
    role === CollaboratorRoleEnum.MANAGER
      ? { coordinator_profile: controls.coordinatorProfile.value }
      : {}),
    ...(role === CollaboratorRoleEnum.MANAGER ? { gives_pin: true } : {}),
    ...(operates
      ? {
          pdv_sales_scope: controls.pdvSalesScope.value,
          cash_limit_action: controls.cashLimitAction.value,
          sales_limit_cents:
            controls.salesLimit.value == null
              ? null
              : Math.round(controls.salesLimit.value * 100),
        }
      : {}),
  };
}

@Component({
  selector: "app-pdv-access-fields",
  imports: [ReactiveFormsModule, InputNumberModule],
  styleUrl: "./pdv-access-fields.component.scss",
  template: `
    <div class="pdv-access">
      <div class="pdv-access__field">
        <label class="form-label" for="role">Cargo</label>
        <select
          id="role"
          class="form-select"
          [attr.aria-describedby]="
            roleDescription ? 'collaboratorRoleHelp' : null
          "
          [formControl]="controls.role">
          <option value="" disabled>
            Selecione um cargo para o colaborador
          </option>
          @if (!operatorsOnly) {
            <option value="manager">Gerente</option>
            <option value="pdv_portaria_coordenador">
              Coordenador PDV Evento
            </option>
          }
          <option value="pdv_portaria">PDV Evento</option>
          @if (!operatorsOnly) {
            <option value="doorperson">Porteiro</option>
            <option value="marketing">Marketing</option>
            <option value="observer">Observador</option>
          }
        </select>
        @if (roleDescription) {
          <p id="collaboratorRoleHelp">{{ roleDescription }}</p>
        }
      </div>
      @if (
        controls.role.value === "pdv_portaria_coordenador" ||
        controls.role.value === "manager"
      ) {
        <div class="pdv-access__field">
          <label class="form-label" for="coordinatorProfile"
            >Atuação no PDV</label
          >
          <select
            id="coordinatorProfile"
            class="form-select"
            [formControl]="controls.coordinatorProfile">
            <option value="approver_only">Somente autoriza com PIN</option>
            <option value="also_operates">
              Autoriza com PIN e também vende
            </option>
          </select>
          @if (operates) {
            <p>
              Pode usar o próprio PIN para autorizar suas operações de caixa.
            </p>
          } @else {
            <p>Autoriza operações de caixa com PIN, sem realizar vendas.</p>
          }
        </div>
      }
      @if (operates) {
        <div class="pdv-access__field pdv-access__field--wide">
          <label class="form-label" for="pdvSalesScope"
            >Permissão de venda neste evento</label
          >
          <select
            id="pdvSalesScope"
            class="form-select"
            [formControl]="controls.pdvSalesScope">
            <option value="portaria_only">Somente portaria</option>
            <option value="advance_and_portaria">Antecipados + portaria</option>
          </select>
          <p>
            A taxa é definida pelo lote. Esta permissão não dá acesso a outros
            eventos.
          </p>
        </div>
        <div class="pdv-access__field">
          <label class="form-label" for="salesLimit"
            >Limite de dinheiro no caixa (R$)</label
          >
          <p-inputnumber
            inputId="salesLimit"
            class="pdv-access__currency"
            inputStyleClass="form-control"
            [inputStyle]="{ width: '100%', minWidth: '0' }"
            mode="currency"
            currency="BRL"
            locale="pt-BR"
            [minFractionDigits]="2"
            [maxFractionDigits]="2"
            [allowEmpty]="true"
            [formControl]="controls.salesLimit"
            placeholder="Sem limite" />
          <p>
            Troco + vendas em dinheiro + suprimentos − sangrias. Não inclui
            cartão/PIX. Deixe em branco para não definir limite, em qualquer
            modo.
          </p>
          @if (controls.salesLimit.invalid && controls.salesLimit.touched) {
            <p role="alert">
              Informe um valor positivo de até R$ 21.474.836,47 ou deixe em
              branco para não definir limite.
            </p>
          }
        </div>
        <div class="pdv-access__field">
          <label class="form-label" for="cashLimitAction"
            >Ao atingir o limite</label
          >
          <select
            id="cashLimitAction"
            class="form-select"
            [formControl]="controls.cashLimitAction">
            <option value="notify_only">Somente avisar</option>
            <option value="block_sales">Avisar e bloquear novas vendas</option>
          </select>
          @if (controls.cashLimitAction.value === "block_sales") {
            <p>
              Com um limite definido, novas vendas ficam bloqueadas até uma
              sangria com PIN baixar o saldo abaixo do limite. O caixa não é
              fechado; pagamentos já iniciados podem ser registrados.
            </p>
          } @else {
            <p>
              O sistema avisa os responsáveis para realizar sangria, sem
              bloquear novas vendas.
            </p>
          }
        </div>
      }
    </div>
  `,
})
export class PdvAccessFieldsComponent implements OnChanges {
  @Input({ required: true }) controls!: PdvAccessControls;
  @Input() operatorsOnly = false;
  private readonly destroyRef = inject(DestroyRef);
  private boundControls?: PdvAccessControls;
  private readonly roleDescriptions: Partial<
    Record<CollaboratorRoleEnum, string>
  > = {
    [CollaboratorRoleEnum.MANAGER]:
      "Pode editar o evento e gerenciar lotes, pedidos, relatórios e colaboradores, incluindo coordenadores. Autoriza operações do PDV com PIN e só vende quando habilitado em “Atuação no PDV”.",
    [CollaboratorRoleEnum.PDV_PORTARIA_COORDINATOR]:
      "Pode supervisionar caixas, consultar relatórios do PDV, autorizar operações com PIN e convidar operadores PDV Evento. Só vende quando habilitado em “Atuação no PDV”. Não pode editar o evento.",
    [CollaboratorRoleEnum.PDV_PORTARIA]:
      "Pode vender, consultar vendas e reimprimir ingressos do próprio caixa, conforme a permissão definida. Só a abertura antecipada dispensa PIN; abertura da portaria, fechamento, sangria e suprimento exigem PIN de um responsável. Não pode editar o evento nem gerenciar colaboradores.",
    [CollaboratorRoleEnum.DOORPERSON]:
      "Pode consultar e validar ingressos na entrada deste evento. Não pode editar o evento, vender ingressos, operar caixas ou gerenciar colaboradores.",
    [CollaboratorRoleEnum.MARKETING]:
      "Pode configurar integrações de marketing e acompanhar campanhas e conversões deste evento, sem acesso a valores financeiros. Não pode editar o evento, operar caixas ou gerenciar colaboradores.",
    [CollaboratorRoleEnum.OBSERVER]:
      "Pode visualizar o painel do evento, inclusive valores, relatórios e cupons, sem convidar, editar, cancelar ou alterar vínculos de marketing.",
  };
  get roleDescription() {
    return this.roleDescriptions[
      this.controls.role.value as CollaboratorRoleEnum
    ];
  }
  get operates() {
    return (
      this.controls.role.value === "pdv_portaria" ||
      ((this.controls.role.value === "pdv_portaria_coordenador" ||
        this.controls.role.value === "manager") &&
        this.controls.coordinatorProfile.value === "also_operates")
    );
  }
  ngOnChanges(changes: SimpleChanges) {
    this.verifyControls(changes);
  }
  private verifyControls(changes: SimpleChanges) {
    if (
      !hasInputChanged<PdvAccessFieldsComponent, "controls">(
        changes,
        "controls",
      ) ||
      this.boundControls === this.controls
    )
      return;
    this.boundControls = this.controls;
    this.validateLimit();
    merge(
      this.controls.role.valueChanges,
      this.controls.coordinatorProfile.valueChanges,
      this.controls.cashLimitAction.valueChanges,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.validateLimit()),
      )
      .subscribe();
  }
  private validateLimit() {
    this.controls.salesLimit.setValidators(
      this.operates
        ? [
            Validators.min(0.01),
            Validators.max(21474836.47),
            Validators.pattern(/^\d+(\.\d{1,2})?$/),
          ]
        : [],
    );
    this.controls.salesLimit.updateValueAndValidity({ emitEvent: false });
  }
}
